import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6'];

const QuestionAnalysis = ({ survey }) => {
  const responses = survey.responses || [];
  const questions = survey.questions || [];

  const getQuestionStats = (question) => {
    const answers = responses.map(r => r.answers?.[question.id]).filter(a => a !== undefined && a !== null && a !== '');
    
    switch (question.type) {
      case 'multipleChoice':
      case 'dropdown':
      case 'yesNo':
        return getChoiceStats(question, answers);
      case 'checkboxes':
        return getCheckboxStats(question, answers);
      case 'linearScale':
      case 'likertScale':
        return getScaleStats(question, answers);
      case 'shortAnswer':
      case 'longAnswer':
        return getTextStats(question, answers);
      case 'number':
        return getNumberStats(answers);
      default:
        return null;
    }
  };

  const getChoiceStats = (question, answers) => {
    const counts = {};
    const options = question.settings?.options || question.options || [];
    options.forEach(opt => {
      counts[opt.id] = { id: opt.id, text: opt.text, count: 0 };
    });
    if (question.type === 'yesNo') {
      counts['yes'] = { id: 'yes', text: 'Yes', count: 0 };
      counts['no'] = { id: 'no', text: 'No', count: 0 };
    }
    
    answers.forEach(a => {
      if (counts[a]) counts[a].count++;
    });

    const total = answers.length;
    const data = Object.values(counts).map(item => ({
      ...item,
      percentage: total > 0 ? Math.round((item.count / total) * 100) : 0
    }));

    return { type: 'choice', data, total };
  };

  const getCheckboxStats = (question, answers) => {
    const counts = {};
    const options = question.settings?.options || question.options || [];
    options.forEach(opt => {
      counts[opt.id] = { id: opt.id, text: opt.text, count: 0 };
    });

    answers.forEach(answerArray => {
      if (Array.isArray(answerArray)) {
        answerArray.forEach(a => {
          if (counts[a]) counts[a].count++;
        });
      }
    });

    const total = answers.length;
    const data = Object.values(counts).map(item => ({
      ...item,
      percentage: total > 0 ? Math.round((item.count / total) * 100) : 0
    }));

    return { type: 'checkbox', data, total };
  };

  const getScaleStats = (question, answers) => {
    const numericAnswers = answers.map(a => typeof a === 'number' ? a : parseInt(a)).filter(n => !isNaN(n));
    
    if (numericAnswers.length === 0) {
      return { type: 'scale', data: [], avg: 0, total: 0 };
    }

    const avg = numericAnswers.reduce((sum, n) => sum + n, 0) / numericAnswers.length;
    const settings = question.settings || {};
    
    const distribution = {};
    if (question.type === 'linearScale') {
      const min = settings.scaleMin || question.scaleMin || 1;
      const max = settings.scaleMax || question.scaleMax || 5;
      for (let i = min; i <= max; i++) {
        distribution[i] = 0;
      }
    } else if (question.type === 'likertScale') {
      const points = settings.points || question.points || 5;
      for (let i = 1; i <= points; i++) {
        distribution[i] = 0;
      }
    }

    numericAnswers.forEach(n => {
      if (distribution[n] !== undefined) distribution[n]++;
    });

    const labels = settings.labels || question.labels || [];
    const data = Object.entries(distribution).map(([value, count]) => {
      const label = question.type === 'likertScale' && labels.length > 0
        ? labels[parseInt(value) - 1] || value 
        : value;
      return {
        value: parseInt(value),
        label: String(label),
        count,
        percentage: Math.round((count / numericAnswers.length) * 100)
      };
    });

    const scaleMin = settings.scaleMin || question.scaleMin || 1;
    const scaleMax = settings.scaleMax || question.scaleMax || (labels.length || 5);

    return { 
      type: 'scale', 
      data, 
      avg: avg.toFixed(2), 
      total: numericAnswers.length,
      min: scaleMin,
      max: scaleMax
    };
  };

  const getTextStats = (question, answers) => {
    return { type: 'text', responses: answers, total: answers.length };
  };

  const getNumberStats = (answers) => {
    const nums = answers.map(a => parseFloat(a)).filter(n => !isNaN(n));
    if (nums.length === 0) return { type: 'number', total: 0 };
    
    const avg = nums.reduce((s, n) => s + n, 0) / nums.length;
    const min = Math.min(...nums);
    const max = Math.max(...nums);
    
    return { type: 'number', avg: avg.toFixed(2), min, max, total: nums.length };
  };

  const renderChart = (question, stats) => {
    if (!stats || stats.total === 0) {
      return <div className="no-data">No responses yet</div>;
    }

    switch (stats.type) {
      case 'choice':
        return (
          <div className="chart-with-table">
            <div className="chart-container" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.data}
                    dataKey="count"
                    nameKey="text"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ text, percentage }) => `${text}: ${percentage}%`}
                    labelLine={true}
                  >
                    {stats.data.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} responses`, name]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <table className="stats-table">
              <thead>
                <tr>
                  <th>Option</th>
                  <th>Responses</th>
                  <th>Percentage</th>
                </tr>
              </thead>
              <tbody>
                {stats.data.map((item, i) => (
                  <tr key={i}>
                    <td>{item.text}</td>
                    <td>{item.count}</td>
                    <td>
                      <div className="percentage-bar">
                        <div className="percentage-bar-track">
                          <div 
                            className="percentage-bar-fill" 
                            style={{ width: `${item.percentage}%` }}
                          />
                        </div>
                        <span className="percentage-value">{item.percentage}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'checkbox':
        return (
          <div className="chart-with-table">
            <div className="chart-container" style={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.data} layout="vertical" margin={{ left: 100, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="text" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="chart-note">Note: Percentages may exceed 100% as respondents can select multiple options.</p>
          </div>
        );

      case 'scale':
        return (
          <div className="scale-analysis">
            <div className="avg-rating">
              <div className="avg-rating-value">{stats.avg}</div>
              <div className="avg-rating-info">
                <div className="avg-rating-label">Average Rating</div>
                <div className="avg-rating-range">Scale: {stats.min} - {stats.max}</div>
              </div>
            </div>
            <div className="chart-container" style={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.data} margin={{ top: 20, right: 20, bottom: 40, left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="label" 
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value} responses`, 'Count']} />
                  <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="text-responses-list">
            <div className="text-summary">{stats.total} text response{stats.total !== 1 ? 's' : ''}</div>
            {stats.responses.slice(0, 10).map((text, i) => (
              <div key={i} className="text-response-item">"{text}"</div>
            ))}
            {stats.responses.length > 10 && (
              <div className="text-more">+{stats.responses.length - 10} more responses</div>
            )}
          </div>
        );

      case 'number':
        return (
          <div className="number-stats">
            <div className="number-stat">
              <div className="number-stat-value">{stats.avg}</div>
              <div className="number-stat-label">Average</div>
            </div>
            <div className="number-stat">
              <div className="number-stat-value">{stats.min}</div>
              <div className="number-stat-label">Minimum</div>
            </div>
            <div className="number-stat">
              <div className="number-stat-value">{stats.max}</div>
              <div className="number-stat-label">Maximum</div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="question-analysis-list">
      {questions.map((question, index) => {
        const stats = getQuestionStats(question);
        return (
          <div key={question.id} className="question-analysis-item">
            <div className="question-analysis-header">
              <div className="question-analysis-number">Question {index + 1}</div>
              <h3 className="question-analysis-text">{question.text}</h3>
            </div>
            {renderChart(question, stats)}
          </div>
        );
      })}

      <style>{`
        .chart-with-table {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .chart-note {
          font-size: 12px;
          color: #64748b;
          font-style: italic;
          margin: 8px 0 0;
        }
        .scale-analysis {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }
        .avg-rating-info {
          display: flex;
          flex-direction: column;
        }
        .avg-rating-range {
          font-size: 12px;
          color: #94a3b8;
        }
        .text-summary {
          font-size: 14px;
          color: #64748b;
          margin-bottom: 12px;
        }
        .text-more {
          font-size: 13px;
          color: #6366f1;
          font-style: italic;
          padding: 12px 16px;
        }
        .no-data {
          text-align: center;
          padding: 40px;
          color: #94a3b8;
        }
        .number-stats {
          display: flex;
          gap: 24px;
          padding: 16px;
        }
        .number-stat {
          text-align: center;
          padding: 16px 24px;
          background: white;
          border-radius: 8px;
        }
        .number-stat-value {
          font-size: 28px;
          font-weight: 700;
          color: #6366f1;
        }
        .number-stat-label {
          font-size: 13px;
          color: #64748b;
          margin-top: 4px;
        }
      `}</style>
    </div>
  );
};

export default QuestionAnalysis;
